/**
 * Tone — the application's semantic color palette.
 *
 * A `Tone` is a named color slot (e.g. `'green'`, `'red'`, `'amber'`) that any
 * UI surface can ask for without hard-coding Tailwind classes. The actual
 * styles live as utility classes in `src/app/globals.css` under the
 * `@layer utilities` block (see the "Tones" section there).
 *
 * Each tone has 9 slots; the lookup maps below expose them as typed
 * `Record<Tone, string>` objects so TypeScript guarantees exhaustive coverage
 * when a new tone is added.
 *
 *   TONE_BADGE        — standard soft data-badge
 *   TONE_BADGE_STRONG — stronger variant for severe states
 *   TONE_OUTLINE      — outline-only variant (border + text, no bg)
 *   TONE_BORDER       — soft border only (matches the tile ring weight, no text)
 *   TONE_DOT          — small status / indicator dot
 *   TONE_TEXT         — text color (same as `text-tone-*`)
 *   TONE_TILE         — gradient bg + inset ring (avatar / icon tile)
 *   TONE_ACTIVE_BG    — subtle gradient for active nav rows
 *   TONE_ACCENT_BAR   — solid 2-px left-edge accent
 *   TONE_LABEL        — small uppercase scope label
 *
 *   TONE_HEX          — hex color pairs for libraries that consume hex
 *                       (maps, 3D viz, charts). Read as `TONE_HEX[tone][theme]`.
 *
 * Usage:
 *
 *   import { TONE_BADGE, type Tone } from '@/lib/tone'
 *
 *   const tone: Tone = 'green'
 *   <Badge className={TONE_BADGE[tone]}>Active</Badge>
 */

/**
 * Single source of truth for tone names. Adding a tone is a 3-step process:
 * 1. Append the name here.
 * 2. Add the 9 utility classes under `@layer utilities` in globals.css.
 * 3. Add the hex pair to TONE_HEX below.
 *
 * The `Tone` union type is derived from this list so it stays in sync.
 */
export const TONES = [
  'blue',
  'emerald',
  'amber',
  'yellow',
  'red',
  'orange',
  'green',
  'purple',
  'violet',
  'zinc',
  'gray',
  'indigo',
  'cyan',
  'teal',
  'lime',
  'rose'
] as const

export type Tone = (typeof TONES)[number]

function toneClassMap(slot: string): Record<Tone, string> {
  return Object.fromEntries(
    TONES.map((t) => [t, `tone-${t}-${slot}`])
  ) as Record<Tone, string>
}

export const TONE_BADGE = toneClassMap('badge')
export const TONE_BADGE_STRONG = toneClassMap('badge-strong')
export const TONE_OUTLINE = toneClassMap('outline')
export const TONE_BORDER = toneClassMap('border')
export const TONE_DOT = toneClassMap('dot')
export const TONE_TILE = toneClassMap('tile')
export const TONE_ACTIVE_BG = toneClassMap('active-bg')
export const TONE_ACCENT_BAR = toneClassMap('accent-bar')
export const TONE_LABEL = toneClassMap('label')

/** Every tone slot name, for class-string translation between slots. */
const TONE_SLOTS = [
  'badge-strong',
  'badge',
  'outline',
  'border',
  'dot',
  'tile',
  'active-bg',
  'accent-bar',
  'label'
] as const

// `badge-strong` must precede `badge` — the regex alternation is ordered, and
// a leading `badge` would match the prefix of `badge-strong` and leave `-strong`
// stranded on the end.
const TONE_SLOT_PATTERN = new RegExp(
  `\\btone-(${TONES.join('|')})-(${TONE_SLOTS.join('|')})\\b`,
  'g'
)

/**
 * Re-slot a tone class string: `tone-emerald-badge` → `tone-emerald-dot`.
 *
 * For callers that receive a tone class from somewhere else and need it in a
 * different visual role. The motivating case is the table filter rail, which
 * paints an 8px dot with whatever class a facet's `colorMap` supplies — and
 * every consumer supplies `TONE_BADGE`, whose `bg-*-200` is a *fill* shade meant
 * to sit behind dark text. On a bare dot it reads washed out next to the same
 * value's badge in the table body. Translating to the `dot` slot restores the
 * intended `bg-*-600/70`.
 *
 * Only recognized `tone-<tone>-<slot>` tokens are rewritten; anything else in
 * the string (a bare `bg-muted-foreground`, a one-off utility) passes through
 * untouched, so this is safe to apply to arbitrary class strings.
 */
export function toneSlot(classes: string, slot: (typeof TONE_SLOTS)[number]) {
  return classes.replace(
    TONE_SLOT_PATTERN,
    (_m, tone) => `tone-${tone}-${slot}`
  )
}

/**
 * `text-tone-{name}` rather than `tone-{name}-text` — kept under the `text-`
 * prefix so shadcn's `[&_svg:not([class*='text-'])]` heuristic picks it up
 * and auto-applies icon color inside `<Button>` / `<SidebarMenuButton>`.
 */
export const TONE_TEXT: Record<Tone, string> = Object.fromEntries(
  TONES.map((t) => [t, `text-tone-${t}`])
) as Record<Tone, string>

/**
 * Hex color pairs for consumers that can't use Tailwind classes (Leaflet maps,
 * React-Three-Fiber 3D scenes, Recharts, etc.). Values are the Tailwind
 * palette's `-600` shade in light mode and `-400` in dark mode — chosen to
 * match the perceived weight of the `dot` slot in each theme.
 */
export const TONE_HEX: Record<Tone, { light: string; dark: string }> = {
  blue: { light: '#2563EB', dark: '#60A5FA' },
  emerald: { light: '#059669', dark: '#34D399' },
  amber: { light: '#D97706', dark: '#FBBF24' },
  yellow: { light: '#CA8A04', dark: '#FACC15' },
  red: { light: '#DC2626', dark: '#F87171' },
  orange: { light: '#EA580C', dark: '#FB923C' },
  green: { light: '#16A34A', dark: '#4ADE80' },
  purple: { light: '#9333EA', dark: '#C084FC' },
  violet: { light: '#7C3AED', dark: '#A78BFA' },
  zinc: { light: '#52525B', dark: '#A1A1AA' },
  gray: { light: '#4B5563', dark: '#9CA3AF' },
  indigo: { light: '#4F46E5', dark: '#818CF8' },
  cyan: { light: '#0891B2', dark: '#22D3EE' },
  teal: { light: '#0D9488', dark: '#2DD4BF' },
  lime: { light: '#65A30D', dark: '#A3E635' },
  rose: { light: '#E11D48', dark: '#FB7185' }
}

/**
 * Resolve a tone's hex color for the active theme. `theme` defaults to
 * `'light'` since most map/chart consumers are not yet theme-reactive.
 */
export function toneHex(tone: Tone, theme: 'light' | 'dark' = 'light'): string {
  return TONE_HEX[tone][theme]
}

/**
 * Stable "random" tone-tile class for a given seed string. Same seed always
 * returns the same tone — used by avatar fallbacks, user chips, and anywhere
 * a deterministic-but-varied tile color is desired.
 *
 * `palette` defaults to the 7-tone scope-branding set; pass a custom subset
 * (e.g. `['emerald', 'cyan']`) when an area's design system restricts colors.
 */
const DEFAULT_HASH_PALETTE: readonly Tone[] = [
  'blue',
  'emerald',
  'amber',
  'red',
  'orange',
  'green',
  'purple'
]

export function hashToneTile(
  seed: string,
  palette: readonly Tone[] = DEFAULT_HASH_PALETTE
): string {
  return TONE_TILE[hashTone(seed, palette)]
}

/**
 * The tone itself, for callers that need more than the tile class — a matching
 * border, an active background, a hex for a chart.
 *
 * `hashToneTile` is this plus a `TONE_TILE` lookup. Callers wanting a second
 * class from the same seed take this rather than re-deriving the hash, so an
 * avatar and the control around it can never disagree about their color.
 */
export function hashTone(
  seed: string,
  palette: readonly Tone[] = DEFAULT_HASH_PALETTE
): Tone {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash)
  }
  return palette[Math.abs(hash) % palette.length]
}
